// ts-rest contract for the KYC/KYB vendor adapter endpoints.
//
// Five endpoints:
//   GET  /api/integrations/kyc/status
//   POST /api/integrations/kyc/screen-individual
//   POST /api/integrations/kyc/screen-entity
//   POST /api/integrations/kyc/applicant
//   POST /api/integrations/kyc/webhook/:vendor

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

// ---------------------------------------------------------------------------
// Vendor enum
// ---------------------------------------------------------------------------

export const kycVendorSchema = z.enum(['comply_advantage', 'sumsub', 'smartsearch', 'credas']);
export type KycVendor = z.infer<typeof kycVendorSchema>;

// ---------------------------------------------------------------------------
// Screen result — returned by screenIndividual and screenEntity
// ---------------------------------------------------------------------------

export const kycMatchSchema = z.object({
    id: z.string(),
    name: z.string(),
    matchType: z.string(), // e.g. "sanctions", "pep", "adverse_media"
    score: z.number(), // 0–100 vendor confidence
    countries: z.array(z.string()),
    detail: z.string().optional(),
});
export type KycMatch = z.infer<typeof kycMatchSchema>;

export const kycScreenStatusSchema = z.enum(['clear', 'review', 'hit']);
export type KycScreenStatus = z.infer<typeof kycScreenStatusSchema>;

export const kycScreenResultSchema = z.object({
    searchId: z.string(),
    clientId: z.string(),
    vendor: kycVendorSchema,
    status: kycScreenStatusSchema,
    /** The vendor's own word for the answer, for example `refer` (SEP-228 §5.3). */
    vendorOutcome: z.string().nullable().optional(),
    matchCount: z.number(),
    matches: z.array(kycMatchSchema),
    screenedAt: z.string(), // ISO timestamp
    raw: z.unknown().optional(), // vendor-specific raw payload (omitted in demo)
});
export type KycScreenResult = z.infer<typeof kycScreenResultSchema>;

// ---------------------------------------------------------------------------
// Applicant — returned by fetchApplicant
// ---------------------------------------------------------------------------

export const kycApplicantSchema = z.object({
    applicantId: z.string(),
    clientId: z.string(),
    vendor: kycVendorSchema,
    status: z.string(), // vendor-specific status string
    reviewResult: z.string().nullable(),
    createdAt: z.string(), // ISO timestamp
    updatedAt: z.string(), // ISO timestamp
});
export type KycApplicant = z.infer<typeof kycApplicantSchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const kycScreenIndividualRequestSchema = z.object({
    clientId: z.string(),
    name: z.string(),
    dateOfBirth: z.string().optional(), // ISO date
    nationality: z.string().optional(), // ISO 3166-1 alpha-2
    vendor: kycVendorSchema.optional(), // override default vendor
});
export type KycScreenIndividualRequest = z.infer<typeof kycScreenIndividualRequestSchema>;

export const kycScreenEntityRequestSchema = z.object({
    clientId: z.string(),
    companyName: z.string(),
    registrationNumber: z.string().optional(),
    countryOfRegistration: z.string().optional(), // ISO 3166-1 alpha-2
    vendor: kycVendorSchema.optional(), // override default vendor
});
export type KycScreenEntityRequest = z.infer<typeof kycScreenEntityRequestSchema>;

export const kycApplicantRequestSchema = z.object({
    applicantId: z.string(),
    vendor: kycVendorSchema,
});
export type KycApplicantRequest = z.infer<typeof kycApplicantRequestSchema>;

export const kycWebhookPayloadSchema = z.object({
    event: z.string(), // e.g. "search_completed", "applicant_reviewed"
    searchId: z.string().optional(),
    applicantId: z.string().optional(),
    data: z.unknown().optional(),
});
export type KycWebhookPayload = z.infer<typeof kycWebhookPayloadSchema>;

// ---------------------------------------------------------------------------
// Status response
// ---------------------------------------------------------------------------

/**
 * One capability of the closed set (SEP-263 D9).
 *
 * Firm configuration maps each capability to exactly one provider. A capability
 * with no provider, or with a provider that holds no key, is not available, and
 * no other provider serves it.
 */
export const kycCapabilitySchema = z.enum([
    'idv',
    'sanctions_peps',
    'source_of_funds',
    'bank_account',
    'company',
    'address_mortality',
    'esign',
]);
export type KycCapability = z.infer<typeof kycCapabilitySchema>;

export const kycVendorStatusSchema = z.object({
    configured: z.boolean(),
    /** The capabilities that this provider serves for the firm today. */
    serves: z.array(kycCapabilitySchema),
});
export type KycVendorStatus = z.infer<typeof kycVendorStatusSchema>;

export const kycCapabilityStatusSchema = z.object({
    capability: kycCapabilitySchema,
    /** The provider that the firm chose, or null when it chose none. */
    provider: kycVendorSchema.nullable(),
    available: z.boolean(),
});
export type KycCapabilityStatus = z.infer<typeof kycCapabilityStatusSchema>;

export const kycStatusResponseSchema = z.object({
    vendors: z.object({
        complyAdvantage: kycVendorStatusSchema,
        sumsub: kycVendorStatusSchema,
        smartsearch: kycVendorStatusSchema,
        credas: kycVendorStatusSchema,
    }),
    capabilities: z.array(kycCapabilityStatusSchema),
});
export type KycStatusResponse = z.infer<typeof kycStatusResponseSchema>;

// ---------------------------------------------------------------------------
// SmartSearch identity verification (SEP-228 §5, §12, §34)
// ---------------------------------------------------------------------------
//
// The identity verification is not a screening. A screening reports sanctions
// and adverse media for Compliance. This door verifies WHO a person is, and its
// answer opens or holds the AML questionnaire. The two share the vendor facade
// and the screening row, so they share this contract file.

/** What the vendor said. The vocabulary of `AmlIdentityVerification` (§5). */
export const smartsearchIdentityOutcomeSchema = z.enum(['verified', 'cannot_identify']);
export type SmartsearchIdentityOutcome = z.infer<typeof smartsearchIdentityOutcomeSchema>;

/** The gate status the outcome moved the row to. */
export const smartsearchVerificationStatusSchema = z.enum([
    'pending',
    'verified',
    'compliance_review_required',
    'awaiting_documents',
    'cannot_verify',
]);
export type SmartsearchVerificationStatus = z.infer<typeof smartsearchVerificationStatusSchema>;

/**
 * One person to verify. An individual contact needs no entry at all: the door
 * verifies the subject itself. A company names each applicable director, owner
 * and trustee (§20.6).
 */
export const smartsearchVerifyPersonSchema = z.object({
    /** The row to re-check. Absent on the first check of a person. */
    verificationId: z.string().uuid().optional(),
    /** The Contact of the person, when the firm holds one. */
    personContactId: z.string().optional(),
    /** The person's name, when no Contact holds it. */
    personName: z.string().optional(),
    /** Why the person is applicable, for example `director`. */
    personRole: z.string().optional(),
});
export type SmartsearchVerifyPerson = z.infer<typeof smartsearchVerifyPersonSchema>;

export const smartsearchVerifyRequestSchema = z.object({
    contactId: z.string().uuid(),
    /** Empty or absent verifies the subject Contact alone. */
    people: z.array(smartsearchVerifyPersonSchema).optional(),
});
export type SmartsearchVerifyRequest = z.infer<typeof smartsearchVerifyRequestSchema>;

export const smartsearchVerificationOutcomeSchema = z.object({
    verificationId: z.string(),
    personName: z.string(),
    personRole: z.string().nullable(),
    outcome: smartsearchIdentityOutcomeSchema,
    status: smartsearchVerificationStatusSchema,
    /** The vendor's own word for the answer, beside the firm's vocabulary. */
    vendorResult: z.string(),
    searchId: z.string(),
    /** Null means the vendor gave no reliable adverse-media answer (§12). */
    adverseMedia: z.boolean().nullable(),
    screenStatus: kycScreenStatusSchema,
});
export type SmartsearchVerificationOutcome = z.infer<typeof smartsearchVerificationOutcomeSchema>;

export const smartsearchVerifyResponseSchema = z.object({
    contactId: z.string(),
    /** One outcome per person the run verified. A company gives the whole set. */
    outcomes: z.array(smartsearchVerificationOutcomeSchema),
});
export type SmartsearchVerifyResponse = z.infer<typeof smartsearchVerifyResponseSchema>;

/** One ongoing due-diligence alert from the vendor (§34). */
export const smartsearchOngoingAlertRequestSchema = z.object({
    alertId: z.string(),
    /** The search the alert belongs to. It names the person the firm checked. */
    searchId: z.string(),
    alertType: z.string(),
    summary: z.string(),
    raisedAt: z.string().optional(),
    /** The subject, when the caller knows it. Otherwise the search names it. */
    contactId: z.string().uuid().optional(),
});
export type SmartsearchOngoingAlertRequest = z.infer<typeof smartsearchOngoingAlertRequestSchema>;

export const smartsearchOngoingAlertResponseSchema = z.object({
    ok: z.literal(true),
    contactId: z.string(),
    /** True when the alert raised a compliance review action (§34). */
    reviewRaised: z.boolean(),
});
export type SmartsearchOngoingAlertResponse = z.infer<typeof smartsearchOngoingAlertResponseSchema>;

// ---------------------------------------------------------------------------
// The Compliance identity review (SEP-228 §6, §7)
// ---------------------------------------------------------------------------
//
// Compliance decides a case the vendor could not. Each decision is one door,
// because each carries different mandatory input. The doors dispatch the
// verification Actions, and each landing raises or clears the §37 priority
// actions.

/** One verification row, as the Compliance review reads it. */
export const identityVerificationDtoSchema = z.object({
    id: z.string(),
    contactId: z.string(),
    /** The person the row verifies. Null means the subject Contact itself. */
    personName: z.string().nullable(),
    personRole: z.string().nullable(),
    status: smartsearchVerificationStatusSchema,
    /** The vendor that answered, or null on a row no vendor has answered. */
    vendor: z.string().nullable(),
    /**
     * How the answer reached the platform. `manual_entry` means a person ran the
     * check in the vendor's own web application and typed the result in, so no
     * API call backs the row. A null reads as `vendor_api`.
     */
    entrySource: z.enum(['vendor_api', 'manual_entry']).nullable(),
    vendorOutcome: smartsearchIdentityOutcomeSchema.nullable(),
    adverseMedia: z.boolean().nullable(),
    /** What Compliance asked for, when the row awaits documents (§6). */
    evidenceRequested: z.array(z.enum(['identity', 'address'])),
    reason: z.string().nullable(),
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
});
export type IdentityVerificationDto = z.infer<typeof identityVerificationDtoSchema>;

export const listIdentityVerificationsResponseSchema = z.object({
    contactId: z.string(),
    verifications: z.array(identityVerificationDtoSchema),
    /**
     * Whether the temporary manual door is open (SEP-228 §5). It is open while
     * the firm holds no SmartSearch credentials, and it closes by itself on the
     * day they land. The card reads it to decide whether to offer the control,
     * and the door refuses the call in all cases, so the card can never open a
     * form the server then refuses.
     */
    manualEntryAvailable: z.boolean(),
});
export type ListIdentityVerificationsResponse = z.infer<
    typeof listIdentityVerificationsResponseSchema
>;

/** §6 Option 1 and §7 — Compliance verifies the person. A reason is mandatory. */
export const identityVerifyRequestSchema = z.object({
    reason: z.string().min(1),
    /** The documents Compliance accepted, when the case awaited them (§7). */
    evidenceDocumentIds: z.array(z.string()).optional(),
});
export type IdentityVerifyRequest = z.infer<typeof identityVerifyRequestSchema>;

/** §6 Option 2 — Compliance asks the desk pair for further evidence. */
export const identityRequestEvidenceRequestSchema = z.object({
    reason: z.string().min(1),
    evidenceRequested: z.array(z.enum(['identity', 'address'])).min(1),
});
export type IdentityRequestEvidenceRequest = z.infer<typeof identityRequestEvidenceRequestSchema>;

/** Compliance records that the person cannot be verified. */
export const identityRefuseRequestSchema = z.object({
    reason: z.string().min(1),
});
export type IdentityRefuseRequest = z.infer<typeof identityRefuseRequestSchema>;

export const identityDecisionResponseSchema = z.object({
    ok: z.literal(true),
    id: z.string(),
    contactId: z.string(),
    status: smartsearchVerificationStatusSchema,
});
export type IdentityDecisionResponse = z.infer<typeof identityDecisionResponseSchema>;

// ---------------------------------------------------------------------------
// The temporary manual door (SEP-228, the "TEMP FIX UNTIL SmartSearch API"
// comment of 20 August 2026)
// ---------------------------------------------------------------------------
//
// The firm holds no SmartSearch credentials yet, so Ops runs the check in the
// vendor's own web application and records the word it gave. A `pass` opens the
// AML questionnaire. A `refer` holds it and raises the Compliance review, and
// Ops hands the SmartSearch report over outside the platform.
//
// The platform never asks for that report. The temp fix is explicit: uploading a
// SmartSearch document must not be a condition of recording either outcome or of
// moving the client into Compliance review.

/** One person a manual record covers. It mirrors `smartsearchVerifyPersonSchema`. */
export const externalIdentityPersonSchema = z.object({
    /** The row to re-record. Absent on the first record of a person. */
    verificationId: z.string().uuid().optional(),
    personContactId: z.string().uuid().optional(),
    personName: z.string().optional(),
    personRole: z.string().optional(),
});
export type ExternalIdentityPerson = z.infer<typeof externalIdentityPersonSchema>;

/** The word SmartSearch gave the person who ran the check (temp fix §1). */
export const externalIdentityOutcomeSchema = z.enum(['pass', 'refer']);
export type ExternalIdentityOutcome = z.infer<typeof externalIdentityOutcomeSchema>;

export const recordExternalIdentityCheckRequestSchema = z.object({
    contactId: z.string().uuid(),
    /** Empty means the subject Contact itself. A company names its people. */
    people: z.array(externalIdentityPersonSchema).optional(),
    /**
     * `pass` verifies the person. `refer` holds the questionnaire and raises the
     * Compliance review. The caller states the vendor's word, never a status.
     */
    outcome: externalIdentityOutcomeSchema,
    /**
     * What the person saw. OPTIONAL: the temp fix lists the outcome, the time,
     * the user and the reference as the whole of what to capture, and it forbids
     * any further condition standing between Ops and the record.
     */
    note: z.string().trim().min(1).nullable().optional(),
    /** The SmartSearch search reference, when the vendor gave one. */
    vendorSearchId: z.string().trim().min(1).nullable().optional(),
});
export type RecordExternalIdentityCheckRequest = z.infer<
    typeof recordExternalIdentityCheckRequestSchema
>;

export const recordExternalIdentityCheckResponseSchema = z.object({
    ok: z.literal(true),
    contactId: z.string(),
    /** The rows the record created or moved, so the caller can refetch by id. */
    verificationIds: z.array(z.string()),
    /** Where the record left the rows. A `refer` holds the questionnaire shut. */
    status: smartsearchVerificationStatusSchema,
});
export type RecordExternalIdentityCheckResponse = z.infer<
    typeof recordExternalIdentityCheckResponseSchema
>;

/** The refusal the manual door gives once SmartSearch has its credentials. */
export const manualEntryClosedSchema = z.object({
    error: z.string(),
    code: z.literal('vendor_configured'),
});

/** The refusal a run gets while the SmartSearch key is absent. */
export const smartsearchNotConfiguredSchema = z.object({
    error: z.string(),
    code: z.literal('not_configured'),
    /** The environment variable the operator must set. */
    missingKey: z.string(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const kycContract = c.router(
    {
        status: {
            method: 'GET',
            path: '/integrations/kyc/status',
            responses: {
                200: kycStatusResponseSchema,
            },
            summary: 'Return KYC vendor configuration status',
        },

        screenIndividual: {
            method: 'POST',
            path: '/integrations/kyc/screen-individual',
            body: kycScreenIndividualRequestSchema,
            responses: {
                200: kycScreenResultSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Screen an individual client (default: ComplyAdvantage)',
        },

        screenEntity: {
            method: 'POST',
            path: '/integrations/kyc/screen-entity',
            body: kycScreenEntityRequestSchema,
            responses: {
                200: kycScreenResultSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Screen a corporate entity client (default: Sumsub Auto-KYB)',
        },

        fetchApplicant: {
            method: 'POST',
            path: '/integrations/kyc/applicant',
            body: kycApplicantRequestSchema,
            responses: {
                200: kycApplicantSchema,
                404: z.object({ error: z.string() }),
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Fetch an applicant record from the specified vendor',
        },

        webhook: {
            method: 'POST',
            path: '/integrations/kyc/webhook/:vendor',
            pathParams: z.object({ vendor: kycVendorSchema }),
            body: kycWebhookPayloadSchema,
            responses: {
                200: z.object({ ok: z.boolean() }),
            },
            summary: 'Receive KYC vendor webhook events (idempotent)',
        },

        // --- SmartSearch identity verification (SEP-228) -----------------

        smartsearchVerify: {
            method: 'POST',
            path: '/integrations/kyc/smartsearch/verify',
            body: smartsearchVerifyRequestSchema,
            responses: {
                200: smartsearchVerifyResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
                503: smartsearchNotConfiguredSchema,
            },
            summary: 'Run the SmartSearch identity check and hand the outcome to the AML gate',
        },

        smartsearchOngoingAlert: {
            method: 'POST',
            path: '/integrations/kyc/smartsearch/ongoing-alert',
            body: smartsearchOngoingAlertRequestSchema,
            responses: {
                200: smartsearchOngoingAlertResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Turn one ongoing due-diligence alert into compliance review work',
        },

        // --- The Compliance identity review (SEP-228 §6, §7) -------------

        listIdentityVerifications: {
            method: 'GET',
            path: '/integrations/kyc/identity-verifications',
            query: z.object({ contactId: z.string().uuid() }),
            responses: {
                200: listIdentityVerificationsResponseSchema,
                404: staffActionErrorSchema,
            },
            summary: 'List the identity verification rows of one Contact',
        },

        recordExternalIdentityCheck: {
            method: 'POST',
            path: '/integrations/kyc/identity-verifications/record-external',
            body: recordExternalIdentityCheckRequestSchema,
            responses: {
                200: recordExternalIdentityCheckResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                409: manualEntryClosedSchema,
            },
            summary: 'Record a SmartSearch check run outside the platform (SEP-228 §5, temporary)',
        },

        identityVerify: {
            method: 'POST',
            path: '/integrations/kyc/identity-verifications/:id/verify',
            pathParams: z.object({ id: z.string().uuid() }),
            body: identityVerifyRequestSchema,
            responses: {
                200: identityDecisionResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance verifies a person the vendor could not (§6, §7)',
        },

        identityRequestEvidence: {
            method: 'POST',
            path: '/integrations/kyc/identity-verifications/:id/request-evidence',
            pathParams: z.object({ id: z.string().uuid() }),
            body: identityRequestEvidenceRequestSchema,
            responses: {
                200: identityDecisionResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance asks for further identity or address evidence (§6)',
        },

        identityRefuse: {
            method: 'POST',
            path: '/integrations/kyc/identity-verifications/:id/refuse',
            pathParams: z.object({ id: z.string().uuid() }),
            body: identityRefuseRequestSchema,
            responses: {
                200: identityDecisionResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Compliance records that a person cannot be verified (§6)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
